// Top Module Dock interactions — docs/Menus-Management/menu-design.md §10, §12, §50.
//
// Click, tap and Enter are handled by the markup (each module is a link). This
// hook adds the interactions that need JavaScript:
//
//   - wheel / trackpad / touch swipe scrolling along the dock
//   - overflow chevrons that appear only when the dock actually overflows
//   - roving-tabindex keyboard navigation (arrows, Home, End)
//
// Gestures are never the only way to reach a module (§51): every module is a
// focusable link, and the arrow keys duplicate every gesture.

const SCROLL_STEP = 240;

const ModuleDock = {
  mounted() {
    this.track = this.el.querySelector("[data-dock-track]");
    this.prevBtn = this.el.querySelector('[data-dock-scroll="prev"]');
    this.nextBtn = this.el.querySelector('[data-dock-scroll="next"]');

    this.onWheel = this.handleWheel.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onScroll = this.updateChevrons.bind(this);
    this.onResize = this.updateChevrons.bind(this);

    this.track.addEventListener("wheel", this.onWheel, { passive: false });
    this.track.addEventListener("scroll", this.onScroll, { passive: true });
    this.el.addEventListener("keydown", this.onKeydown);
    window.addEventListener("resize", this.onResize);

    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", () => this.scrollBy(-SCROLL_STEP));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", () => this.scrollBy(SCROLL_STEP));
    }

    this.scrollActiveIntoView();
    this.updateChevrons();
  },

  updated() {
    this.scrollActiveIntoView();
    this.updateChevrons();
  },

  destroyed() {
    this.track.removeEventListener("wheel", this.onWheel);
    this.track.removeEventListener("scroll", this.onScroll);
    this.el.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.onResize);
  },

  items() {
    return Array.from(this.el.querySelectorAll("[data-dock-item]"));
  },

  // Vertical wheel gestures scroll the dock horizontally, which is what a
  // trackpad swipe over a dock is expected to do. Horizontal wheel events are
  // left to the browser's native handling.
  handleWheel(event) {
    if (!this.overflowing()) return;

    const vertical = Math.abs(event.deltaY) > Math.abs(event.deltaX);
    if (!vertical) return;

    event.preventDefault();
    this.track.scrollLeft += event.deltaY;
  },

  handleKeydown(event) {
    const items = this.items();
    const currentIndex = items.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex = null;

    switch (event.key) {
      case "ArrowRight":
        nextIndex = Math.min(currentIndex + 1, items.length - 1);
        break;
      case "ArrowLeft":
        nextIndex = Math.max(currentIndex - 1, 0);
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = items.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    // Roving tabindex: only the focused module stays in the tab order, so Tab
    // moves past the whole dock rather than through all eleven modules.
    items.forEach((item, index) => {
      item.setAttribute("tabindex", index === nextIndex ? "0" : "-1");
    });

    items[nextIndex].focus();
    items[nextIndex].scrollIntoView({ block: "nearest", inline: "nearest" });
  },

  scrollBy(amount) {
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  },

  overflowing() {
    return this.track.scrollWidth > this.track.clientWidth + 1;
  },

  scrollActiveIntoView() {
    const active = this.el.querySelector('[data-dock-item][aria-selected="true"]');
    if (active) {
      active.scrollIntoView({ block: "nearest", inline: "center" });
    }
  },

  // Chevrons are shown only when there is somewhere to scroll, so the dock does
  // not carry dead controls at wide viewports (§12).
  updateChevrons() {
    if (!this.prevBtn || !this.nextBtn) return;

    const overflowing = this.overflowing();
    const atStart = this.track.scrollLeft <= 1;
    const atEnd =
      this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;

    this.toggle(this.prevBtn, overflowing && !atStart);
    this.toggle(this.nextBtn, overflowing && !atEnd);
  },

  toggle(el, visible) {
    if (visible) {
      el.setAttribute("data-visible", "");
    } else {
      el.removeAttribute("data-visible");
    }
  },
};

export default ModuleDock;
